// Client preview for Named variants CSV/JSON bulk import (header fields only).

import { parseBulkImport } from "./bulkImportParser";
import type { NamedVariantStatus, NamedVariantSummary } from "./namedVariantsClient";

const VALID_STATUSES = new Set<NamedVariantStatus>(["draft", "active", "archived"]);

export type NamedVariantImportRow = {
  variant_code: string;
  variant_name: string;
  description: string;
  status: NamedVariantStatus;
  exists: boolean;
  blocked: boolean;
  blockReason: string | null;
};

export type NamedVariantsBulkImportPreview =
  | { kind: "empty" }
  | { kind: "error"; error: string }
  | {
      kind: "ok";
      variants: NamedVariantImportRow[];
      totals: { create: number; update: number; blocked: number };
    };

function cell(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function parseStatus(raw: string): NamedVariantStatus | null {
  const s = raw.toLowerCase();
  if (VALID_STATUSES.has(s as NamedVariantStatus)) return s as NamedVariantStatus;
  return null;
}

export function buildNamedVariantsBulkImportPreview(
  text: string,
  existingVariants: ReadonlyArray<NamedVariantSummary>,
): NamedVariantsBulkImportPreview {
  const trimmed = text.trim();
  if (!trimmed) return { kind: "empty" };

  const parsed = parseBulkImport(trimmed);
  if (!parsed.ok) return { kind: "error", error: parsed.error };

  const byCode = new Map(existingVariants.map((v) => [v.code.trim().toLowerCase(), v]));
  const grouped = new Map<string, NamedVariantImportRow>();

  for (const raw of parsed.rows) {
    const variant_code = cell(raw, "variant_code") || cell(raw, "code");
    const variant_name = cell(raw, "variant_name") || cell(raw, "name");
    if (!variant_code) {
      return { kind: "error", error: "Each row needs variant_code." };
    }

    const statusRaw = cell(raw, "status");
    const status = statusRaw ? parseStatus(statusRaw) : ("draft" as NamedVariantStatus);

    let row = grouped.get(variant_code.toLowerCase());
    if (!row) {
      row = {
        variant_code,
        variant_name: variant_name || variant_code,
        description: cell(raw, "description"),
        status: status ?? "draft",
        exists: byCode.has(variant_code.toLowerCase()),
        blocked: false,
        blockReason: null,
      };
      grouped.set(variant_code.toLowerCase(), row);
    } else {
      if (variant_name) row.variant_name = variant_name;
      if (cell(raw, "description")) row.description = cell(raw, "description");
      if (statusRaw && status) row.status = status;
    }

    if (statusRaw && !status) {
      row.blocked = true;
      row.blockReason = `Invalid status "${statusRaw}" (use draft, active, or archived).`;
    }
  }

  const variants = [...grouped.values()];
  return {
    kind: "ok",
    variants,
    totals: {
      create: variants.filter((v) => !v.exists && !v.blocked).length,
      update: variants.filter((v) => v.exists && !v.blocked).length,
      blocked: variants.filter((v) => v.blocked).length,
    },
  };
}
